/**
 * Call dispositions: the desk's wrap-up "Outcome" codes, and what each one does to the CRM.
 *
 * The codes and their wording are the desk's (tomorrow-life-phone-system,
 * src/config/dispositions.ts); this file is the other half of that contract. A code the desk
 * sends that is not listed here is still filed on the call as it was sent — the call record is a
 * record of what the agent said happened — it just changes nothing else.
 *
 * Effects are deliberately conservative. A wrap-up is thirty seconds of an agent's attention, so
 * it may move work forward and file what the agent typed, but it never moves a policy backwards,
 * never out of Closed Won or Closed Lost, and never deletes anything. The one effect that is not
 * conservative is DNC, on purpose: a revocation of consent is effective the moment it is said
 * (TCPA), so it writes to the do-not-call list in the same transaction as the call.
 */

import type { DisqualifyReason, LeadStatus, LossReason, PolicyStage } from "./domain";

export type DispositionGroup =
  "Won" | "In flight" | "In play" | "Not sold" | "Blocked" | "No contact" | "System";

export interface DispositionEffect {
  /** Where an open lead behind the call goes. Converted and disqualified leads are left alone. */
  lead?: { status: LeadStatus; disqualifyReason?: DisqualifyReason };
  /**
   * Where the caller's open policy (Stage Issued, the sale waiting on its first draft) goes.
   * `create` makes one, sold today by the agent on the call, when the caller has none open. Only
   * ever forward: Closed Won and Closed Lost are where a policy stops.
   */
  policy?: { stage: PolicyStage; create: boolean; lossReason?: LossReason };
  /** Put the number on the internal do-not-call list and mark the contact do-not-call. */
  doNotCall?: true;
}

export interface Disposition {
  code: string;
  group: DispositionGroup;
  label: string;
  effect: DispositionEffect;
}

export const DISPOSITIONS: readonly Disposition[] = [
  {
    code: "SUBMIT",
    group: "Won",
    label: "Application submitted",
    effect: { lead: { status: "converted" }, policy: { stage: "issued", create: true } },
  },
  {
    code: "PLACED",
    group: "Won",
    label: "Policy delivered and paid",
    effect: { lead: { status: "converted" }, policy: { stage: "closed_won", create: true } },
  },
  {
    code: "PENDAPS",
    group: "In flight",
    label: "Pending records or exam",
    effect: { lead: { status: "converted" }, policy: { stage: "issued", create: true } },
  },
  {
    code: "QUOTED",
    group: "In play",
    label: "Illustration presented",
    effect: { lead: { status: "contacted" } },
  },
  {
    code: "APPT",
    group: "In play",
    label: "Appointment set",
    effect: { lead: { status: "contacted" } },
  },
  {
    code: "CBHLD",
    group: "In play",
    label: "Callback, no firm time",
    effect: { lead: { status: "contacted" } },
  },
  {
    code: "NI",
    group: "Not sold",
    label: "Not interested",
    effect: { lead: { status: "disqualified", disqualifyReason: "not_interested" } },
  },
  {
    code: "BUDGET",
    group: "Not sold",
    label: "Lost on budget",
    effect: { lead: { status: "disqualified", disqualifyReason: "not_interested" } },
  },
  {
    code: "UNINS",
    group: "Not sold",
    label: "Declined or heavily rated",
    // An application the carrier declined: the open sale is lost. Never creates one.
    effect: { policy: { stage: "closed_lost", create: false, lossReason: "Other" } },
  },
  {
    code: "NOPROD",
    group: "Not sold",
    label: "No carrier writes this risk",
    effect: { lead: { status: "disqualified", disqualifyReason: "no_product" } },
  },
  {
    // "Transfer, do not quote": the lead is still good for a licensed agent, so it stays open.
    code: "NOLIC",
    group: "Blocked",
    label: "Not licensed in state",
    effect: { lead: { status: "contacted" } },
  },
  {
    code: "NOCNS",
    group: "Blocked",
    label: "No valid consent",
    effect: { lead: { status: "disqualified", disqualifyReason: "no_consent" } },
  },
  {
    code: "DNC",
    group: "Blocked",
    label: "Do not call, requested",
    effect: {
      lead: { status: "disqualified", disqualifyReason: "do_not_call" },
      doNotCall: true,
    },
  },
  {
    code: "VM",
    group: "No contact",
    label: "Voicemail left",
    effect: { lead: { status: "working" } },
  },
  {
    code: "NA",
    group: "No contact",
    label: "No answer",
    effect: { lead: { status: "working" } },
  },
  {
    code: "WN",
    group: "System",
    label: "Wrong number",
    effect: { lead: { status: "disqualified", disqualifyReason: "wrong_number" } },
  },
];

export function findDisposition(code: string | null | undefined): Disposition | null {
  return DISPOSITIONS.find((d) => d.code === code) ?? null;
}

export function dispositionLabel(code: string | null | undefined): string {
  if (!code) return "";
  return findDisposition(code)?.label ?? code;
}
